/**
 * 화면 상태의 **단일 파생점** (DESIGN §5 "단일 JobStatus").
 *
 * 상단 pill · 본문 카드 · 우측 드로어가 각자 상태를 판단하면 셋이 어긋난다 — 검토에서 지적된
 * "오류인데 진행 중으로 보이는" 화면이 정확히 그 결과였다. 그래서 표면은 여기서 한 번만 정하고,
 * 세 곳은 그 값을 **읽기만** 한다.
 *
 * 입력이 둘인 이유: 이 앱에는 축이 둘이다 — 대화 턴(SSE 스트림)과 저작 job(폴링). 우선순위는
 *  ① 진행 중인 저작 job (8~9분짜리라 화면의 주된 사실이다)
 *  ② 대화 턴(스트리밍 · 게이트 대기 · 스트림 오류)
 *  ③ 끝난 job 의 결과
 * 순이다. 판단은 순수 함수로 남긴다(테스트가 브라우저 없이 이 표를 지킨다).
 */
import { isActive } from '@/jobs/types'
import type { JobStatus, JobView } from '@/jobs/types'
import type { RunStatus } from './events'

export type Surface =
  | { kind: 'idle' }
  | { kind: 'streaming' }
  | { kind: 'awaiting_gate' }
  | { kind: 'stream_error' }
  | { kind: 'job'; status: JobStatus }

export function deriveSurface(job: JobView | null | undefined, run: RunStatus): Surface {
  if (job && isActive(job)) return { kind: 'job', status: job.status }
  if (run === 'streaming') return { kind: 'streaming' }
  if (run === 'awaiting_gate') return { kind: 'awaiting_gate' }
  if (run === 'error') return { kind: 'stream_error' }
  if (job) return { kind: 'job', status: job.status }
  return { kind: 'idle' }
}

/** pill 표기 — `null` 이면 pill 을 그리지 않는다(할 말이 없을 때 뱃지를 만들지 않는다). */
export interface PillView {
  /** app.css 의 pill 변형 클래스. 빈 문자열 = 기본(액센트). */
  tone: '' | 'okp' | 'errp' | 'warnp' | 'mute'
  /** i18n 키. */
  key: string
  /** 진행 중임을 나타내는 점멸 점. */
  pulse: boolean
}

const never = (value: never): never => {
  throw new Error(`unhandled surface: ${JSON.stringify(value)}`)
}

export function pillOf(surface: Surface): PillView | null {
  switch (surface.kind) {
    case 'idle':
      return null
    case 'streaming':
      return { tone: '', key: 'surface.streaming', pulse: true }
    case 'awaiting_gate':
      return { tone: 'errp', key: 'surface.awaiting_gate', pulse: false }
    case 'stream_error':
      return { tone: 'errp', key: 'surface.stream_error', pulse: false }
    case 'job':
      return jobPill(surface.status)
    default:
      return never(surface)
  }
}

function jobPill(status: JobStatus): PillView {
  switch (status) {
    case 'queued':
      return { tone: 'mute', key: 'surface.job_queued', pulse: false }
    case 'running':
      return { tone: '', key: 'surface.job_running', pulse: true }
    case 'complete':
      return { tone: 'okp', key: 'surface.job_complete', pulse: false }
    case 'failed':
      return { tone: 'errp', key: 'surface.job_failed', pulse: false }
    case 'cancelled':
      return { tone: 'mute', key: 'surface.job_cancelled', pulse: false }
    default:
      return never(status)
  }
}

/** 드로어 요약 한 줄 — pill 과 **같은 값에서** 나온다. */
export interface DrawerLine {
  tone: 'run' | 'ok' | 'err' | 'warn' | 'mute'
  key: string
}

export function drawerLineOf(surface: Surface): DrawerLine {
  switch (surface.kind) {
    case 'idle':
      return { tone: 'mute', key: 'surface.idle' }
    case 'streaming':
      return { tone: 'run', key: 'surface.streaming' }
    case 'awaiting_gate':
      return { tone: 'err', key: 'surface.awaiting_gate' }
    case 'stream_error':
      return { tone: 'err', key: 'surface.stream_error' }
    case 'job':
      switch (surface.status) {
        case 'queued':
          return { tone: 'mute', key: 'surface.job_queued' }
        case 'running':
          return { tone: 'run', key: 'surface.job_running' }
        case 'complete':
          return { tone: 'ok', key: 'surface.job_complete' }
        case 'failed':
          return { tone: 'err', key: 'surface.job_failed' }
        case 'cancelled':
          return { tone: 'mute', key: 'surface.job_cancelled' }
        default:
          return never(surface.status)
      }
    default:
      return never(surface)
  }
}
